import {Helmet} from '@dr.pogodin/react-helmet';
import {addDays} from 'date-fns';
import {Action, Card, DataStatus, Form, FormSubmitHandler} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';
import {isFalsy, isNotNil} from 'ramda-adjunct';

import {useGetBillingInformationV2Query} from '@dms/api/base';
import {useGetTenantQuery} from '@dms/api/core';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {useGetInvoiceDraftQuery} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {useGetCurrentBranch, useGetInitialDocumentSeriesId} from '@dms/shared';

import {buildArray, getApiDateString} from 'shared/web';

import {CustomerAndSupplier} from '../../components/CustomerAndSupplier';
import {DocumentSeriesSelect} from '../../components/DocumentSeriesSelect';
import {FormHeader} from '../../components/FormHeader';
import {InvoicePage} from '../../components/InvoicePage';
import {ListOfItemsForm} from '../../components/listOfItems/ListOfItemsForm';
import {PaymentDates} from '../../components/PaymentDates';
import {PaymentDetails} from '../../components/PaymentDetails';
import {PaymentsNotes} from '../../components/PaymentsNotes';
import {getEmptyItem, STANDARD_INVOICE_DUE} from '../../constants';
import {InvoiceVatRateProvider} from '../../context/InvoiceVatRateProvider';
import {AccountingDocumentFormValues} from '../../types';
import {getBankAccountDetails} from '../../utils/getBankAccountDetails';
import {getDefaultBankAccountFromBranch} from '../../utils/getDefaultBankAccountFromBranch';
import {createInvoiceOrDraftSchema} from './createInvoiceOrDraftSchema';
import {getInitialValuesFromDraft} from './getInitialValuesFromDraft';
import {useSubmitDraft} from './hooks/useSubmitDraft';
import {useSubmitInvoice} from './hooks/useSubmitInvoice';

export function CreateInvoice() {
  const {id: draftId} = useParams();

  const [handleDraftSubmit] = useSubmitDraft();
  const [handleInvoiceSubmit] = useSubmitInvoice();

  const {
    data: draftInvoice,
    isLoading: isLoadingDraftInvoice,
    isError: isDraftInvoiceError,
  } = useGetInvoiceDraftQuery({invoiceDraftId: draftId ?? ''}, {skip: isNil(draftId)});

  const {isLoading: isLoadingCurrentUser, isError: isGetCurrentUserError} =
    useGetCurrentUserInfoQuery();
  const {isLoading: isLoadingTenant, isError: isGetTenantError} = useGetTenantQuery();
  const currentBranchData = useGetCurrentBranch();
  const {isLoading: isLoadingBillingInfo} = useGetBillingInformationV2Query(
    {id: currentBranchData?.data?.billingInformation?.id ?? ''},
    {skip: isNil(currentBranchData?.data?.billingInformation?.id)}
  );

  const defaultBankAccountResult = getDefaultBankAccountFromBranch(
    null,
    currentBranchData.data,
    currentBranchData.isSynced,
    false
  );

  const bankAccountDetails = getBankAccountDetails(
    defaultBankAccountResult.selectedBankAccount,
    defaultBankAccountResult.availableBankAccounts
  );

  const {documentSeriesId, isLoadingDocumentSeries, isDocumentSeriesError} =
    useGetInitialDocumentSeriesId('invoice');

  const handleCreateInvoiceOrDraft: FormSubmitHandler<AccountingDocumentFormValues> = async (
    formData
  ) => {
    const shouldSubmitDraft = formData.submitType === 'CREATE_DRAFT';
    shouldSubmitDraft
      ? await handleDraftSubmit(formData as AccountingDocumentFormValues)
      : await handleInvoiceSubmit(formData);
  };

  const draftInvoiceDefaultValues = getInitialValuesFromDraft(draftInvoice, documentSeriesId);

  const initialValues: Partial<AccountingDocumentFormValues> = {
    issueDate: getApiDateString(new Date()),
    dueSinceIssueDateInDays: STANDARD_INVOICE_DUE,
    documentSeriesId,
    paymentInfo: {
      paymentMethod: 'BANK_TRANSFER',
      bankAccount: defaultBankAccountResult.selectedBankAccount,
      iban: bankAccountDetails.iban,
      swift: bankAccountDetails.swift,
    },
    dueDate: getApiDateString(addDays(new Date(), STANDARD_INVOICE_DUE)),
    dateOfTaxableSupply: getApiDateString(new Date()),
    invoiceItems: [getEmptyItem()],
    exchangeRateRatio: {
      currency: 'EUR',
      type: i18n.t('general.actions.recalculate'),
      amount: '1',
      ratio: null,
    },
    isExchangeRateRatioEnabled: false,
  };

  const isLoading =
    isLoadingDocumentSeries ||
    isLoadingCurrentUser ||
    isLoadingTenant ||
    isLoadingDraftInvoice ||
    isLoadingBillingInfo;

  const isError =
    isDraftInvoiceError || isGetCurrentUserError || isGetTenantError || isDocumentSeriesError;

  const defaultValues = isNotNil(draftInvoice) ? draftInvoiceDefaultValues : initialValues;

  return (
    <DataStatus isLoading={isLoading} minHeight="80vh" isError={isError}>
      <Helmet title={i18n.t('entity.invoice.labels.newInvoice')} />
      <Form<AccountingDocumentFormValues>
        onSubmit={handleCreateInvoiceOrDraft}
        defaultValues={defaultValues}
        experimentalZodSchema={createInvoiceOrDraftSchema}
      >
        {(control, formApi) => {
          const [paymentMethod, isInternal] = formApi.watch([
            'paymentInfo.paymentMethod',
            'isInternal',
          ]);
          const canCreateDraft = isFalsy(isInternal) && paymentMethod !== 'FREE_OF_CHARGE';

          return (
            <InvoiceVatRateProvider control={control} listItemType="invoicing-documents">
              <VStack>
                <FormHeader
                  title={i18n.t('entity.invoice.labels.newInvoice')}
                  actions={buildArray<Action>()
                    .when(canCreateDraft, {
                      type: 'form-button',
                      control,
                      onClick: () => formApi.setValue('submitType', 'CREATE_DRAFT'),
                      buttonType: 'submit',
                      title: i18n.t('entity.invoice.labels.saveDraft'),
                      variant: 'secondary',
                      'data-testid': testIds.accounting.invoiceNew('create-invoice-draft'),
                    })
                    .add({
                      type: 'form-button',
                      title: i18n.t('general.actions.create'),
                      onClick: () => formApi.setValue('submitType', 'CREATE_NEW'),
                      control,
                      buttonType: 'submit',
                      'data-testid': testIds.accounting.invoiceNew('create-invoice-create'),
                    })}
                />
                <DocumentSeriesSelect
                  control={control}
                  canBeSend
                  documentType="invoice"
                  data-testid={testIds.accounting.invoiceNew('selectDocumentSeries')}
                />

                <InvoicePage>
                  <Card>
                    <VStack spacing={4}>
                      <CustomerAndSupplier
                        formApi={formApi}
                        control={control}
                        allowAdditionalCustomer
                        supplier={draftInvoice?.supplier}
                        data-testid={testIds.accounting.invoiceNew('customer-and-supplier')}
                      />
                      <PaymentDates
                        formApi={formApi}
                        control={control}
                        data-testid={testIds.accounting.invoiceNew('paymentDates')}
                      />
                    </VStack>
                  </Card>

                  <PaymentDetails
                    control={control}
                    formApi={formApi}
                    isDraftMode={isNotNil(draftInvoice)}
                    data-testid={testIds.accounting.invoiceNew('payment-details')}
                  />

                  <ListOfItemsForm
                    control={control}
                    formApi={formApi}
                    listItemType="invoices"
                    data-testid={testIds.accounting.invoiceNew('list-of-items')}
                  />

                  <PaymentsNotes
                    control={control}
                    data-testid={testIds.accounting.invoiceNew('notes')}
                  />
                </InvoicePage>
              </VStack>
            </InvoiceVatRateProvider>
          );
        }}
      </Form>
    </DataStatus>
  );
}
